import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { ToleranceRule, ToleranceScope } from './ToleranceConfiguration';
import { RoundingRule } from '../rounding/RoundingConfiguration';
import { OrgUnitSelector } from '../../settings/orgunit/OrgUnitSelector';
import { flattenOrgUnits, sampleOrgUnits, type OrgUnit } from '../sharedFinancialData';
interface ToleranceFormProps {
  rule: ToleranceRule | null;
  roundingRules: RoundingRule[];
  onSave: (rule: Omit<ToleranceRule, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function ToleranceForm({
  rule,
  roundingRules,
  onSave,
  onCancel,
  locale
}: ToleranceFormProps) {
  const [formData, setFormData] = useState<Omit<ToleranceRule, 'id' | 'createdAt' | 'updatedAt'>>({
    toleranceType: rule?.toleranceType || 'Amount',
    value: rule?.value || 0,
    percentageValue: rule?.percentageValue || 3.0,
    maxAmount: rule?.maxAmount || 500.0,
    roundingId: rule?.roundingId || null,
    roundingName: rule?.roundingName || null,
    scope: rule?.scope || 'Global',
    scopeId: rule?.scopeId || null,
    scopeName: rule?.scopeName || null,
    modules: rule?.modules || ['All'],
    active: rule?.active !== undefined ? rule.active : true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [vendors, setVendors] = useState<{
    id: string;
    name: string;
  }[]>([]);
  const [entities, setEntities] = useState<{
    id: string;
    name: string;
  }[]>([]);
  const [sites, setSites] = useState<{
    id: string;
    name: string;
    entityId: string;
  }[]>([]);
  const [filteredSites, setFilteredSites] = useState<{
    id: string;
    name: string;
    entityId: string;
  }[]>([]);
  // Get OrgUnits from shared data
  const allOrgUnits = flattenOrgUnits(sampleOrgUnits);
  // Load sample data for dropdowns
  useEffect(() => {
    setVendors([{
      id: 'vendor-001',
      name: 'Office Depot'
    }, {
      id: 'vendor-002',
      name: 'Dell Technologies'
    }, {
      id: 'vendor-003',
      name: 'PrintWorks Inc.'
    }]);
    setEntities([{
      id: 'entity-001',
      name: 'Global Technologies Inc.'
    }, {
      id: 'entity-002',
      name: 'European Operations Ltd'
    }]);
    setSites([{
      id: 'site-001',
      name: 'New York HQ',
      entityId: 'entity-001'
    }, {
      id: 'site-002',
      name: 'San Francisco Office',
      entityId: 'entity-001'
    }, {
      id: 'site-003',
      name: 'London Office',
      entityId: 'entity-002'
    }, {
      id: 'site-004',
      name: 'Paris Branch',
      entityId: 'entity-002'
    }]);
  }, []);
  // Update filtered sites when entity changes
  useEffect(() => {
    if (formData.scope === 'Site' && formData.scopeId) {
      const filteredSitesList = sites.filter(site => site.entityId === formData.scopeId);
      setFilteredSites(filteredSitesList);
    }
  }, [formData.scopeId, formData.scope, sites]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scope') {
      // Reset scope-related fields when scope changes
      setFormData({
        ...formData,
        scope: value as ToleranceScope,
        scopeId: null,
        scopeName: null
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    const numberValue = parseFloat(value);
    if (!isNaN(numberValue)) {
      setFormData({
        ...formData,
        [name]: numberValue
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const handleRoundingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      value
    } = e.target;
    if (!value) {
      setFormData({
        ...formData,
        roundingId: null,
        roundingName: null
      });
    } else {
      const selectedRounding = roundingRules.find(r => r.id === value);
      setFormData({
        ...formData,
        roundingId: value,
        roundingName: selectedRounding ? selectedRounding.name : null
      });
    }
  };
  const handleScopeIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      value
    } = e.target;
    let scopeName = null;
    if (value) {
      switch (formData.scope) {
        case 'Vendor':
          const vendor = vendors.find(v => v.id === value);
          scopeName = vendor ? vendor.name : null;
          break;
        case 'Entity':
          const entity = entities.find(e => e.id === value);
          scopeName = entity ? entity.name : null;
          break;
        case 'Site':
          const site = sites.find(s => s.id === value);
          scopeName = site ? site.name : null;
          break;
      }
    }
    setFormData({
      ...formData,
      scopeId: value || null,
      scopeName
    });
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormData({
      ...formData,
      scopeId: orgUnitId || null,
      scopeName: orgUnit?.name || null
    });
    // Clear error
    if (errors.scopeId) {
      setErrors({
        ...errors,
        scopeId: ''
      });
    }
  };
  const handleModulesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    // If 'All Modules' is selected, only keep that option
    if (selectedOptions.includes('All')) {
      setFormData({
        ...formData,
        modules: ['All']
      });
    } else {
      setFormData({
        ...formData,
        modules: selectedOptions
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (formData.toleranceType === 'Amount' && formData.value <= 0) {
      newErrors.value = 'Value must be greater than 0';
    }
    if (formData.toleranceType === 'Percentage' && formData.value <= 0) {
      newErrors.value = 'Percentage must be greater than 0';
    }
    if (formData.toleranceType === 'Combined') {
      if (!formData.percentageValue || formData.percentageValue <= 0) {
        newErrors.percentageValue = 'Percentage must be greater than 0';
      }
      if (!formData.maxAmount || formData.maxAmount <= 0) {
        newErrors.maxAmount = 'Maximum amount must be greater than 0';
      }
    }
    if (formData.scope !== 'Global' && !formData.scopeId) {
      newErrors.scopeId = `${formData.scope} must be selected`;
    }
    if (formData.modules.length === 0) {
      newErrors.modules = 'At least one module must be selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-12862">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12863">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12864">
          {rule ? 'Edit Tolerance Rule' : 'Create Tolerance Rule'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-12865">
          {rule ? 'Update the details of an existing tolerance rule' : 'Define how much deviation is acceptable when matching values'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-12866">
        <div className="px-4 py-5 sm:p-6" data-id="element-12867">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-12868">
            <div className="sm:col-span-3" data-id="element-12869">
              <label htmlFor="toleranceType" className="block text-sm font-medium text-gray-700" data-id="element-12870">
                Tolerance Type <span className="text-red-500" data-id="element-12871">*</span>
              </label>
              <div className="mt-1" data-id="element-12872">
                <select id="toleranceType" name="toleranceType" value={formData.toleranceType} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12873">
                  <option value="Amount" data-id="element-12874">Amount</option>
                  <option value="Percentage" data-id="element-12875">Percentage</option>
                  <option value="Combined" data-id="element-12876">Combined (% with max amount)</option>
                </select>
              </div>
            </div>
            {formData.toleranceType !== 'Combined' ? <div className="sm:col-span-3" data-id="element-12877">
                <label htmlFor="value" className="block text-sm font-medium text-gray-700" data-id="element-12878">
                  Value <span className="text-red-500" data-id="element-12879">*</span>
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12880">
                  {formData.toleranceType === 'Amount' ? <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12881">
                      <span className="text-gray-500 sm:text-sm" data-id="element-12882">$</span>
                    </div> : null}
                  <input type="number" name="value" id="value" value={formData.value} onChange={handleNumberChange} step={formData.toleranceType === 'Amount' ? '0.01' : '0.1'} min="0" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.value ? 'border-red-300' : ''} ${formData.toleranceType === 'Amount' ? 'pl-7' : ''}`} data-id="element-12883" />
                  {formData.toleranceType === 'Percentage' ? <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12884">
                      <span className="text-gray-500 sm:text-sm" data-id="element-12885">%</span>
                    </div> : null}
                </div>
                {errors.value && <p className="mt-1 text-sm text-red-600" data-id="element-12886">{errors.value}</p>}
                <p className="mt-1 text-xs text-gray-500" data-id="element-12887">
                  {formData.toleranceType === 'Amount' ? 'Fixed amount tolerance (e.g., $5.00)' : 'Percentage tolerance (e.g., 2% of total amount)'}
                </p>
              </div> : <>
                <div className="sm:col-span-3" data-id="element-12888">
                  <label htmlFor="percentageValue" className="block text-sm font-medium text-gray-700" data-id="element-12889">
                    Percentage <span className="text-red-500" data-id="element-12890">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12891">
                    <input type="number" name="percentageValue" id="percentageValue" value={formData.percentageValue} onChange={e => {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    setFormData({
                      ...formData,
                      percentageValue: value
                    });
                  }
                  if (errors.percentageValue) {
                    setErrors({
                      ...errors,
                      percentageValue: ''
                    });
                  }
                }} step="0.1" min="0" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.percentageValue ? 'border-red-300' : ''}`} data-id="element-12892" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12893">
                      <span className="text-gray-500 sm:text-sm" data-id="element-12894">%</span>
                    </div>
                  </div>
                  {errors.percentageValue && <p className="mt-1 text-sm text-red-600" data-id="element-12895">
                      {errors.percentageValue}
                    </p>}
                  <p className="mt-1 text-xs text-gray-500" data-id="element-12896">
                    Percentage tolerance (e.g., 3% of total amount)
                  </p>
                </div>
                <div className="sm:col-span-3" data-id="element-12897">
                  <label htmlFor="maxAmount" className="block text-sm font-medium text-gray-700" data-id="element-12898">
                    Maximum Amount <span className="text-red-500" data-id="element-12899">*</span>
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12900">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12901">
                      <span className="text-gray-500 sm:text-sm" data-id="element-12902">$</span>
                    </div>
                    <input type="number" name="maxAmount" id="maxAmount" value={formData.maxAmount} onChange={e => {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value)) {
                    setFormData({
                      ...formData,
                      maxAmount: value
                    });
                  }
                  if (errors.maxAmount) {
                    setErrors({
                      ...errors,
                      maxAmount: ''
                    });
                  }
                }} step="0.01" min="0" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-7 ${errors.maxAmount ? 'border-red-300' : ''}`} data-id="element-12903" />
                  </div>
                  {errors.maxAmount && <p className="mt-1 text-sm text-red-600" data-id="element-12904">
                      {errors.maxAmount}
                    </p>}
                  <p className="mt-1 text-xs text-gray-500" data-id="element-12905">
                    Maximum absolute amount allowed (e.g., $500.00)
                  </p>
                </div>
              </>}
            <div className="sm:col-span-6" data-id="element-12906">
              <label htmlFor="roundingId" className="block text-sm font-medium text-gray-700" data-id="element-12907">
                Rounding Applied (Optional)
              </label>
              <div className="mt-1" data-id="element-12908">
                <select id="roundingId" name="roundingId" value={formData.roundingId || ''} onChange={handleRoundingChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12909">
                  <option value="" data-id="element-12910">No Rounding</option>
                  {roundingRules.map(rule => <option key={rule.id} value={rule.id} data-id="element-12911">
                      {rule.name} ({rule.roundingType} to {rule.interval})
                    </option>)}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-12912">
                Optional rounding rule to apply to the tolerance calculation
              </p>
            </div>
            <div className="sm:col-span-3" data-id="element-12913">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-12914">
                Scope <span className="text-red-500" data-id="element-12915">*</span>
              </label>
              <div className="mt-1" data-id="element-12916">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-12917">
                  <option value="Global" data-id="element-12918">Whole Application</option>
                  <option value="Vendor" data-id="element-12919">Specific Vendor</option>
                  <option value="OrgUnit" data-id="element-12920">Specific Organisation Unit</option>
                </select>
              </div>
            </div>
            {/* Conditional fields based on scope */}
            {formData.scope === 'Vendor' && <div className="sm:col-span-3" data-id="element-12921">
                <label htmlFor="scopeId" className="block text-sm font-medium text-gray-700" data-id="element-12922">
                  Vendor <span className="text-red-500" data-id="element-12923">*</span>
                </label>
                <div className="mt-1" data-id="element-12924">
                  <select id="scopeId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-12925">
                    <option value="" data-id="element-12926">Select a vendor</option>
                    {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-12927">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-12928">
                      {errors.scopeId}
                    </p>}
                </div>
              </div>}
            {formData.scope === 'Entity' && <div className="sm:col-span-3" data-id="element-12929">
                <label htmlFor="scopeId" className="block text-sm font-medium text-gray-700" data-id="element-12930">
                  Entity <span className="text-red-500" data-id="element-12931">*</span>
                </label>
                <div className="mt-1" data-id="element-12932">
                  <select id="scopeId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-12933">
                    <option value="" data-id="element-12934">Select an entity</option>
                    {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-12935">
                        {entity.name}
                      </option>)}
                  </select>
                  {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-12936">
                      {errors.scopeId}
                    </p>}
                </div>
              </div>}
            {formData.scope === 'Site' && <>
                <div className="sm:col-span-3" data-id="element-12937">
                  <label htmlFor="entityId" className="block text-sm font-medium text-gray-700" data-id="element-12938">
                    Entity <span className="text-red-500" data-id="element-12939">*</span>
                  </label>
                  <div className="mt-1" data-id="element-12940">
                    <select id="entityId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-12941">
                      <option value="" data-id="element-12942">Select an entity</option>
                      {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-12943">
                          {entity.name}
                        </option>)}
                    </select>
                    {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-12944">
                        {errors.scopeId}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-12945">
                  <label htmlFor="siteId" className="block text-sm font-medium text-gray-700" data-id="element-12946">
                    Site <span className="text-red-500" data-id="element-12947">*</span>
                  </label>
                  <div className="mt-1" data-id="element-12948">
                    <select id="siteId" name="siteId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} disabled={!formData.scopeId} data-id="element-12949">
                      <option value="" data-id="element-12950">Select a site</option>
                      {filteredSites.map(site => <option key={site.id} value={site.id} data-id="element-12951">
                          {site.name}
                        </option>)}
                    </select>
                    {!formData.scopeId && <p className="mt-1 text-xs text-gray-500" data-id="element-12952">
                        Please select an entity first
                      </p>}
                    {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-12953">
                        {errors.scopeId}
                      </p>}
                  </div>
                </div>
              </>}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-12954">
                <OrgUnitSelector value={formData.scopeId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} error={errors.scopeId} showPath={true} data-id="element-12955" />
              </div>}
            <div className="sm:col-span-6" data-id="element-12956">
              <label htmlFor="modules" className="block text-sm font-medium text-gray-700" data-id="element-12957">
                Applied To Modules <span className="text-red-500" data-id="element-12958">*</span>
              </label>
              <div className="mt-1" data-id="element-12959">
                <select id="modules" name="modules" multiple value={formData.modules} onChange={handleModulesChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.modules ? 'border-red-300' : ''}`} size={4} data-id="element-12960">
                  <option value="All" data-id="element-12961">All Modules</option>
                  <option value="Invoice" data-id="element-12962">Invoice Processing</option>
                  <option value="PO" data-id="element-12963">PO Matching</option>
                  <option value="Budget" data-id="element-12964">Budget Validation</option>
                </select>
                {errors.modules && <p className="mt-1 text-sm text-red-600" data-id="element-12965">{errors.modules}</p>}
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-12966">
                Hold Ctrl/Cmd to select multiple modules. Select "All Modules"
                to apply to all.
              </p>
            </div>
            <div className="sm:col-span-6" data-id="element-12967">
              <div className="flex items-start" data-id="element-12968">
                <div className="flex items-center h-5" data-id="element-12969">
                  <input id="active" name="active" type="checkbox" checked={formData.active} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-12970" />
                </div>
                <div className="ml-3 text-sm" data-id="element-12971">
                  <label htmlFor="active" className="font-medium text-gray-700" data-id="element-12972">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-12973">
                    When checked, this tolerance rule will be active and applied
                    to matching operations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-12974">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12975">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-12976" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12977">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-12978" />
            {rule ? 'Update Rule' : 'Create Rule'}
          </button>
        </div>
      </form>
    </div>;
}